import { FormVersionStatus } from '@common/entities';

const mapApplication = (app: any) => ({
  label: app.name || `Application ${app.id}`,
  value: app.id,
  type: 'application',
  versions: app.versions,
});

const isPublished = (app: any) =>
  app.versions?.some((v: any) => v.status === FormVersionStatus.Active);

const sortByRecentVersion = (a: any, b: any) => {
  if (!a.versions?.length && !b.versions?.length) return 0;
  if (!a.versions?.length) return 1;
  if (!b.versions?.length) return -1;

  const getMostRecentDate = (versions: any[]) =>
    versions.reduce((prevDate, currVersion) => {
      const currentDate = new Date(currVersion.updatedDate);
      return currentDate > prevDate ? currentDate : prevDate;
    }, new Date(0));

  return getMostRecentDate(b.versions).getTime() - getMostRecentDate(a.versions).getTime();
};

export const buildApplicationsGroup = (applicationsData: any[]) => {
  if (!applicationsData?.length) {
    return { label: 'Applications', children: [] };
  }

  const folders = applicationsData.filter((item: any) => item.folder);
  const standaloneApps = applicationsData.filter((item: any) => !item.folder);

  const folderGroups = folders.map((folder: any) => ({
    label: folder.name || folder.folder?.name || `Folder ${folder.id}`,
    value: folder.id,
    type: 'folder',
    children: folder.folder?.Application?.map(mapApplication).sort(sortByRecentVersion) || [],
  }));

  const publishedApps = standaloneApps.filter(isPublished);
  const draftApps = standaloneApps.filter((app) => !isPublished(app));

  const publishedAppItems = publishedApps.map(mapApplication).sort(sortByRecentVersion);
  const draftAppItems = draftApps.map(mapApplication).sort(sortByRecentVersion);

  return {
    label: 'Applications',
    children: [...publishedAppItems, ...draftAppItems, ...folderGroups],
  };
};

export const getAvailableSteps = (
  elements: {
    id: string;
    name: string;
    label: string;
    sequence: number;
    type: string;
    options: any[];
  }[],
  applicationsData: any,
  currentQuestionId: string,
) => {
  if (!currentQuestionId) return [];

  const currentQuestion = elements.find(({ id }) => id === currentQuestionId);
  if (!currentQuestion) {
    // eslint-disable-next-line no-console
    console.warn(`Current question not found: ${currentQuestionId}`);
    return [];
  }

  const filteredElements = elements.filter(
    (el) => !(el.name === 'application_route_opening_message'),
  );

  const messageElements = filteredElements.filter(({ type }) => type === 'static-text');
  const questionElements = filteredElements.filter(({ type }) => type !== 'static-text');

  const messagesGroup = {
    label: 'Messages',
    type: 'messages-group',
    children: messageElements.map((msg, index) => ({
      label: `Message ${index + 1}`,
      value: msg.id,
      type: 'static-text',
    })),
  };

  const qualifyingQuestionsGroup = {
    label: 'Qualifying Questions',
    type: 'questions-group',
    children: questionElements
      .filter(({ sequence }) => sequence > currentQuestion.sequence)
      .map((q) => ({
        label: q.label,
        value: q.id,
        type: q.type,
      })),
  };
  const applicationsGroup = {
    ...buildApplicationsGroup(applicationsData),
    type: 'applications-group',
  };

  return [messagesGroup, qualifyingQuestionsGroup, applicationsGroup];
};
